import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Wrench, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AMC Quote default templates — inclusions, exclusions, terms.
 *
 * These get pre-filled into every new AMC quote dialog, so ops doesn't
 * have to retype the standard library each time. Edits made in the
 * dialog stay local to that one quote; editing here updates the
 * library going forward.
 *
 * Stored as 3 sibling settings keys (`amc_default_inclusions`,
 * `amc_default_exclusions`, `amc_default_terms`) — same as every other
 * settings field so the existing GET/PUT /api/settings pipeline picks
 * them up without any backend change.
 */
export const AmcQuoteDefaultsCard = () => {
  const [form, setForm] = useState({ inclusions: '', exclusions: '', terms: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchCurrent = async () => {
    try {
      const r = await api.get('/settings');
      const s = r.data?.data || {};
      setForm({
        inclusions: s.amc_default_inclusions || '',
        exclusions: s.amc_default_exclusions || '',
        terms: s.amc_default_terms || '',
      });
    } catch {
      // empty form on error — user can still set values from scratch.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCurrent(); }, []);

  const onChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        amc_default_inclusions: form.inclusions.trim(),
        amc_default_exclusions: form.exclusions.trim(),
        amc_default_terms: form.terms.trim(),
      });
      toast.success('AMC quote defaults saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            AMC Quote Defaults
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="settings-amc-defaults-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Wrench className="h-5 w-5 text-primary" />
          AMC Quote Defaults
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Pre-filled into every new AMC quote. One line per item for inclusions/exclusions.
          Ops can still edit these per-quote — this is just the starting library.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Standard inclusions (one per line)</Label>
            <Textarea rows={7} value={form.inclusions} onChange={onChange('inclusions')}
                      data-testid="settings-amc-inclusions"
                      placeholder={'e.g.\nQuarterly server health audit\n24x7 monitoring\nDaily off-site backups'} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Standard exclusions (one per line)</Label>
            <Textarea rows={7} value={form.exclusions} onChange={onChange('exclusions')}
                      data-testid="settings-amc-exclusions"
                      placeholder={'e.g.\nHardware replacement\nThird-party SaaS fees\nOn-site visits'} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Standard terms &amp; conditions</Label>
          <Textarea rows={6} value={form.terms} onChange={onChange('terms')}
                    data-testid="settings-amc-terms"
                    placeholder="Free-form text — appears on the AMC quote PDF as the Terms section." />
        </div>
        <div>
          <Button onClick={handleSave} disabled={saving} data-testid="settings-amc-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AmcQuoteDefaultsCard;
